import React, { useCallback, useState } from 'react';
import QuickCaptureModal from './QuickCaptureModal';
import type { Client } from '../../services/crmService';

interface QuickCaptureFabProps {
  onCreated: (result: Client) => void;
  /** Optional label for screen readers */
  ariaLabel?: string;
  /**
   * Controlled open state (e.g. `/leads?quickCapture=true` deep link).
   * When both are set, the parent owns open/close; otherwise the FAB uses internal state.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Floating action button to open Photo capture (mobile-first; works on desktop).
 */
const QuickCaptureFab: React.FC<QuickCaptureFabProps> = ({
  onCreated,
  ariaLabel = 'Photo capture lead',
  open: openProp,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined && onOpenChange !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (isControlled) onOpenChange(next);
      else setInternalOpen(next);
    },
    [isControlled, onOpenChange]
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-fg shadow-lg hover:opacity-95 focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary md:bottom-8 md:right-8"
        aria-label={ariaLabel}
        title="Photo capture"
      >
        <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
      <QuickCaptureModal open={open} onClose={() => setOpen(false)} onCreated={onCreated} />
    </>
  );
};

export default QuickCaptureFab;
